// @flow

import * as React from 'react';

import { useDispatch } from 'react-redux';

import { createSelector } from 'reselect';

import { selectIsAuthenticatedUser } from '@gandi/authentication';
import {
  selectDomainCreateProducts,
  selectProductsByType,
  unsetProductsWorkingStatus,
  type DecoratedProduct,
  type DomainProduct,
  type ProductKind as TProductKind,
} from '@gandi/cart';
import { mailboxes } from '@gandi/redux-resource-price';
import type { SSEPollerClient } from '@gandi/sse-poller/client';

import { fetchIDNAvailableLanguages as fetchIDNLanguages } from 'src/DomainCreate/modules/actions/idn';
import { select as selectIDNLanguages } from 'src/DomainCreate/modules/selectors/idn';

import { fetchMailboxesPrices } from 'src/MailboxCreate/modules/actions';
import { useProductsFetch } from 'src/MailboxStandaloneCreate/modules/hooks/useProductsFetch';

import { ErrorBoundary } from 'src/helpers/ErrorBoundary';
import { useAppSelector } from 'src/hooks';
import type { FullState } from 'src/types';

import {
  fetchDomainCreateProductsCatalog,
  fetchClaims,
  fetchDomainRenewProductsCatalog,
} from '../../modules/actions';

import { ProductKind } from './ProductKind';
import { selectHasDomainWithIdn } from './modules/selectors';
import {
  selectHasDomainCreateIDNErrors,
  selectProductKinds,
} from './selectors';

import styles from './Products.scss';

const FREE_MAILBOX_KINDS: $ReadOnlyArray<TProductKind> = [
  'DomainCreate',
  'DomainTransfer',
];

/** Check if the product kinds require legacy Mailbox V2 catalog. */
const hasMailboxV2Offers = (productKinds: Array<TProductKind>): boolean =>
  productKinds.some(
    kind => kind.startsWith('Mailbox') && !kind.startsWith('MailboxStandAlone'),
  );

/** Check if the product kinds require the `mailboxsa` catalog
 * (including domains that might trigger a mailbox trial offer).
 */
const hasMailboxStandaloneOffers = (
  productKinds: Array<TProductKind>,
): boolean =>
  productKinds.some(
    kind =>
      FREE_MAILBOX_KINDS.includes(kind) || kind.startsWith('MailboxStandAlone'),
  );

function useFetchMailboxPricesIfNeeded(): void {
  const dispatch = useDispatch();
  const productKinds = useAppSelector(selectProductKinds);

  // MBSA / mailbox-standalone
  const needsMBSAFetch = hasMailboxStandaloneOffers(productKinds);
  useProductsFetch({ enabled: needsMBSAFetch });

  // Legacy mailboxv2
  const needsV2Fetch = hasMailboxV2Offers(productKinds);
  const shouldFetchV2 = useAppSelector(state =>
    needsV2Fetch
      ? mailboxes.priceMailboxesResource.selectShouldFetchOne(
          state,
          'mailboxv2_create',
        )
      : false,
  );
  React.useEffect(() => {
    if (shouldFetchV2 && needsV2Fetch) {
      dispatch(fetchMailboxesPrices());
    }
  }, [dispatch, shouldFetchV2, needsV2Fetch]);
}

// check for id-based equality to stabilize references
const areProductsSameById = (
  prev: Array<DecoratedProduct>,
  next: Array<DecoratedProduct>,
): boolean => {
  if (prev === next) return true;
  if (prev.length !== next.length) return false;
  return prev.every((p, i): boolean => p.id === next[i].id);
};

// stable wrapper for the imported selector
const selectDomainCreateProductsStable: (
  state: FullState,
) => Array<DomainProduct> =
  // $FlowFixMe[incompatible-call] reselect v5 options
  createSelector(
    [selectDomainCreateProducts],
    (products: Array<DomainProduct>): Array<DomainProduct> => products,
    { memoizeOptions: { resultEqualityCheck: areProductsSameById } },
  );

const productTypeProps = { productType: 'domain' };
// local selector for priceable renew products
const selectPriceableDomainRenewProductsStable: (
  state: FullState,
) => Array<DomainProduct> =
  // $FlowFixMe[incompatible-call] reselect v5 options
  createSelector(
    [
      (state: FullState): Array<DecoratedProduct> =>
        selectProductsByType(state, productTypeProps),
    ],
    (domainProducts: Array<DomainProduct>): Array<DomainProduct> =>
      domainProducts.filter(
        (product): boolean => product.process === 'renew' && !product.isPremium,
      ),
    { memoizeOptions: { resultEqualityCheck: areProductsSameById } },
  );

// local selector for priceable create products using the stable base
const selectPriceableCreateProductsStable: (
  state: FullState,
) => Array<DomainProduct> = createSelector(
  [selectDomainCreateProductsStable],
  (domainProducts: Array<DomainProduct>): Array<DomainProduct> =>
    domainProducts.filter((product): boolean => !product.isPremium),
  { memoizeOptions: { resultEqualityCheck: areProductsSameById } },
);

export function Products(): React.MixedElement {
  const productKinds = useAppSelector(selectProductKinds);

  const domainCreateProducts = useAppSelector(selectDomainCreateProductsStable);
  const priceableDomainCreateProducts = useAppSelector(
    selectPriceableCreateProductsStable,
  );
  const priceableDomainRenewProducts = useAppSelector(
    selectPriceableDomainRenewProductsStable,
  );

  const authenticated = useAppSelector(selectIsAuthenticatedUser);
  const IDNLanguages = useAppSelector(selectIDNLanguages);
  const IDNErrors = useAppSelector(selectHasDomainCreateIDNErrors);
  const hasDomainWithIdn = useAppSelector(selectHasDomainWithIdn);

  const dispatch = useDispatch();

  React.useEffect(() => {
    if (!IDNLanguages.length && (IDNErrors || hasDomainWithIdn)) {
      dispatch(fetchIDNLanguages());
    }
  }, [IDNLanguages.length, IDNErrors, hasDomainWithIdn, dispatch]);

  React.useEffect(() => {
    const ssePollers: Array<SSEPollerClient> = [];

    if (priceableDomainCreateProducts.length) {
      ssePollers.push(
        dispatch(
          fetchDomainCreateProductsCatalog(priceableDomainCreateProducts),
        ),
      );
    }

    // fetch claims only if authenticated
    if (!!domainCreateProducts.length && authenticated) {
      ssePollers.push(dispatch(fetchClaims(domainCreateProducts)));
    }

    if (priceableDomainRenewProducts.length) {
      ssePollers.push(
        dispatch(fetchDomainRenewProductsCatalog(priceableDomainRenewProducts)),
      );
    }

    // componentDidUnmount
    return () => {
      ssePollers.forEach(ssePoller => ssePoller.close());
      dispatch(unsetProductsWorkingStatus());
    };
  }, [
    authenticated,
    dispatch,
    domainCreateProducts,
    priceableDomainCreateProducts,
    priceableDomainRenewProducts,
  ]);

  // Fetch mailbox catalog price only once.
  // In case there are domains (which show the mailbox trial banner)
  // or mailbox items in the cart.
  useFetchMailboxPricesIfNeeded();

  return (
    <ul className={styles.root}>
      {productKinds.map(kind => (
        <ErrorBoundary key={kind} fallback={<ProductKind.Error kind={kind} />}>
          <ProductKind kind={kind} />
        </ErrorBoundary>
      ))}
    </ul>
  );
}
